'use client';

import { useCallback, useRef, useEffect } from 'react';
import { isTauri } from '@/lib/platformUtils';

/**
 * Hook to manage Tauri-specific call features:
 * - Always-on-top window during calls
 * - Prevent display sleep during calls
 * - Focus window for incoming calls
 * 
 * These features only activate on Tauri desktop app.
 * On web/mobile, these are no-ops.
 */

// Tauri invoke helper (matches existing pattern from useMonitoring.ts)
async function tauriInvoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  if (!isTauri()) {
    throw new Error('Not running in Tauri');
  }
  const tauri = (window as any).__TAURI_INTERNALS__;
  if (!tauri) {
    throw new Error('Tauri internals not available');
  }
  return tauri.invoke(command, args);
}

async function getCurrentTauriWindow() {
  if (!isTauri()) {
    throw new Error('Not running in Tauri');
  }

  const { getCurrentWindow } = await import('@tauri-apps/api/window');
  return getCurrentWindow();
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as Record<string, unknown>).message ?? '');
  }

  return '';
}

function isAclDeniedWindowCommand(error: unknown): boolean {
  return getErrorMessage(error).includes('not allowed by ACL');
}

export interface TauriCallControls {
  /** Enable call mode: always-on-top + prevent sleep */
  enableCallMode: () => Promise<void>;
  /** Disable call mode: restore normal window behavior */
  disableCallMode: () => Promise<void>;
  /** Focus window (for incoming calls) */
  focusWindow: () => Promise<void>;
  /** Whether currently in Tauri desktop app */
  isTauriDesktop: boolean;
}

export function useTauriCall(): TauriCallControls {
  const isCallModeActive = useRef(false);
  const windowControlsBlockedRef = useRef(false);
  const isTauriDesktop = isTauri();

  const handleWindowAclError = useCallback((action: string, error: unknown): boolean => {
    if (!isAclDeniedWindowCommand(error)) {
      return false;
    }

    if (!windowControlsBlockedRef.current) {
      windowControlsBlockedRef.current = true;
      console.warn(
        `[useTauriCall] Window control command blocked by ACL during ${action}; ` +
          'using sleep-prevention only until the desktop app is rebuilt with updated permissions.',
        error,
      );
    }

    return true;
  }, []);

  // Enable call mode: always-on-top + prevent display sleep
  const enableCallMode = useCallback(async () => {
    if (!isTauriDesktop) return;
    if (isCallModeActive.current) return;

    try {
      if (!windowControlsBlockedRef.current) {
        const tauriWindow = await getCurrentTauriWindow();
        console.log('[useTauriCall] Enabling call mode for window:', tauriWindow.label);

        try {
          await tauriWindow.setAlwaysOnTop(true);
        } catch (error) {
          if (!handleWindowAclError('enableCallMode', error)) {
            throw error;
          }
        }
      }
      
      // Prevent display from sleeping
      await tauriInvoke('prevent_display_sleep', { prevent: true });
      
      isCallModeActive.current = true;
      console.log('[useTauriCall] Call mode enabled');
    } catch (error) {
      console.error('[useTauriCall] Failed to enable call mode:', error);
    }
  }, [handleWindowAclError, isTauriDesktop]);

  // Disable call mode: restore normal behavior
  const disableCallMode = useCallback(async () => {
    if (!isTauriDesktop) return;
    if (!isCallModeActive.current) return;

    try {
      if (!windowControlsBlockedRef.current) {
        const tauriWindow = await getCurrentTauriWindow();
        console.log('[useTauriCall] Disabling call mode for window:', tauriWindow.label);

        try {
          await tauriWindow.setAlwaysOnTop(false);
        } catch (error) {
          if (!handleWindowAclError('disableCallMode', error)) {
            throw error;
          }
        }
      }
      
      // Allow display to sleep again
      await tauriInvoke('prevent_display_sleep', { prevent: false });
      
      isCallModeActive.current = false;
      console.log('[useTauriCall] Call mode disabled');
    } catch (error) {
      console.error('[useTauriCall] Failed to disable call mode:', error);
    }
  }, [handleWindowAclError, isTauriDesktop]);

  // Focus window (for incoming calls)
  const focusWindow = useCallback(async () => {
    if (!isTauriDesktop) return;
    if (windowControlsBlockedRef.current) return;

    try {
      const tauriWindow = await getCurrentTauriWindow();
      console.log('[useTauriCall] Focusing window for incoming call:', tauriWindow.label);

      try {
        await tauriWindow.show();
        await tauriWindow.unminimize();
        await tauriWindow.setFocus();
      } catch (error) {
        if (handleWindowAclError('focusWindow', error)) {
          return;
        }
        throw error;
      }

      console.log('[useTauriCall] Window focused:', tauriWindow.label);
    } catch (error) {
      console.error('[useTauriCall] Failed to focus window:', error);
    }
  }, [handleWindowAclError, isTauriDesktop]);

  // Cleanup on unmount - ensure call mode is disabled
  useEffect(() => {
    return () => {
      if (isCallModeActive.current && isTauriDesktop) {
        console.log('[useTauriCall] Cleanup: disabling call mode on unmount');
        // Fire and forget - we're unmounting anyway
        if (!windowControlsBlockedRef.current) {
          getCurrentTauriWindow()
            .then((tauriWindow) => tauriWindow.setAlwaysOnTop(false))
            .catch(() => {});
        }
        tauriInvoke('prevent_display_sleep', { prevent: false }).catch(() => {});
      }
    };
  }, [isTauriDesktop]);

  return {
    enableCallMode,
    disableCallMode,
    focusWindow,
    isTauriDesktop
  };
}
